<template>
  <Modal :show="showed" hide-buttons @close="setLastSeen">
    <BoxedSvgIcon icon="BugReport" color="orange" class="-mt-16 mb-2" />

    <div class="text-xl text-center grow font-semibold mb-6 mx-8">
      {{ $t('ui.components.services_notification.title') }}
    </div>

    <div v-if="type == 'init'" class="text-center text-secondary text-base">
      <!-- eslint-disable vue/no-v-html -->
      <p
        class="mb-4"
        v-html="$t('ui.components.services_notification.description_init1')"
      />
      <!-- eslint-enable vue/no-v-html -->
      <p class="mb-4">
        <i18n-t
          keypath="ui.components.services_notification.description_init2"
          tag="strong"
          scope="global"
        >
          <template #link>
            <router-link
              :to="{ name: 'AssetsPortfolioView' }"
              class="text-brand hover:underline"
              @click="setLastSeen"
            >
              {{ $t('ui.components.services_notification.assets_portfolio') }}
            </router-link>
          </template>
        </i18n-t>
      </p>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-html="$t('ui.components.services_notification.description_init3')" />
    </div>
    <div
      v-else-if="type == 'added'"
      class="text-center text-secondary text-base"
    >
      <p class="mb-4">
        {{ $t('ui.components.services_notification.description_added_only1') }}
      </p>
      <p class="mb-4">
        <i18n-t
          keypath="ui.components.services_notification.description_added_only2"
          tag="strong"
          scope="global"
        >
          <template #link>
            <router-link
              :to="{ name: 'AssetsPortfolioView' }"
              class="text-brand hover:underline"
              @click="setLastSeen"
            >
              {{ $t('ui.components.services_notification.assets_portfolio') }}
            </router-link>
          </template>
        </i18n-t>
      </p>
      <!-- eslint-disable vue/no-v-html -->
      <p
        v-html="
          $t('ui.components.services_notification.description_added_only3')
        "
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <div
      v-else-if="type == 'removed'"
      class="text-center text-secondary text-base"
    >
      <p class="mb-4">
        {{
          $t('ui.components.services_notification.description_removed_only1')
        }}
      </p>
      <p>
        <i18n-t
          keypath="ui.components.services_notification.description_removed_only2"
          tag="strong"
          scope="global"
        >
          <template #link>
            <router-link
              :to="{ name: 'AssetsCustomEntriesView' }"
              class="text-brand hover:underline"
              @click="setLastSeen"
            >
              {{
                $t(
                  'ui.components.services_notification.assets_sources_custom_entries'
                )
              }}
            </router-link>
          </template>
        </i18n-t>
      </p>
    </div>
    <div
      v-else-if="type == 'both' && activeView == 'first'"
      class="text-center text-secondary text-base"
    >
      <p>
        {{
          $t('ui.components.services_notification.description_added_when_both')
        }}
      </p>
    </div>

    <div
      v-else-if="type == 'both' && activeView == 'second'"
      class="text-center text-secondary text-base"
    >
      <p class="mb-4">
        {{
          $t(
            'ui.components.services_notification.description_removed_when_both1'
          )
        }}
      </p>
      <p>
        <i18n-t
          keypath="ui.components.services_notification.description_removed_when_both2"
          tag="strong"
          scope="global"
        >
          <template #link>
            <router-link
              :to="{ name: 'AssetSourcesView' }"
              class="text-brand hover:underline"
              @click="setLastSeen"
            >
              {{
                $t('ui.components.services_notification.assets_assets_sources')
              }}
            </router-link>
          </template>
        </i18n-t>
      </p>
    </div>

    <ServicesList
      class="mt-6"
      :added="added"
      :removed="removed"
      :type="type"
      :active-view="activeView"
    />

    <div
      v-if="
        type == 'init' ||
        type == 'added' ||
        type == 'removed' ||
        (type == 'both' && activeView == 'second')
      "
      class="text-center text-secondary text-base mt-6"
    >
      <i18n-t
        keypath="ui.components.services_notification.check_full_list"
        tag="p"
        scope="global"
      >
        <template #link>
          <router-link
            :to="{ name: 'SupportedServicesView' }"
            class="text-brand hover:underline"
            @click="setLastSeen"
          >
            <strong>{{ $t('ui.common.here') }}</strong>
          </router-link>
        </template>
      </i18n-t>
    </div>

    <div
      class="flex mt-8"
      :class="
        type == 'both' && activeView == 'second'
          ? 'justify-between'
          : 'justify-end'
      "
    >
      <Button
        v-if="type == 'both' && activeView == 'first'"
        @click="activeView = 'second'"
      >
        {{ $t('ui.common.next') }}
      </Button>
      <template v-else>
        <Button
          v-if="type === 'both'"
          type="secondary"
          @click="activeView = 'first'"
        >
          {{ $t('ui.common.back') }}
        </Button>
        <Button @click="setLastSeen">
          {{ $t('ui.components.services_notification.confirm') }}
        </Button>
      </template>
    </div>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useServicesDeFiStore } from '@/stores/services-defi'

import Button from '@/components/controls/Button'
import Modal from '@/components/misc/Modal'
import { BoxedSvgIcon } from '@/components/misc/SvgIcon'

import ServicesList from './ServicesList.vue'
import { useServicesNotification } from '../composables/services_notification'

import { dateToUTCString } from '@/services/date.service'

import { type NotificationServicesView } from './types'

export default defineComponent({
  components: {
    BoxedSvgIcon,
    Button,
    Modal,
    ServicesList,
  },

  setup() {
    const { added, removed, type } = useServicesNotification()
    return { added, removed, type }
  },

  data() {
    return {
      showed: true,
      seenDate: dateToUTCString(new Date()),
      activeView: 'first' as NotificationServicesView,

      servicesDeFiStore: useServicesDeFiStore(),
    }
  },

  methods: {
    async setLastSeen() {
      // Closed up front: a failed save must not trap the user in the popup.
      this.showed = false
      try {
        await this.servicesDeFiStore.setLastSeen(this.seenDate)
      } catch (error) {
        this.$bus.emit('error', error)
      }
    },
  },
})
</script>
